import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Divider,
  Box,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Stack,
  IconButton,
  Collapse,
  Tooltip,
  Skeleton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  Grid,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import WcIcon from "@mui/icons-material/Wc";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import NatureIcon from "@mui/icons-material/Nature";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Link as RouterLink } from "react-router-dom";

import api, { API_BASE_URL } from "../../../utils/api";
import MiniMap from "../../shared/MiniMap";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "N/A");
const trimOrNA = (v) => (v && String(v).trim() ? String(v).trim() : "N/A");
const trimOrNull = (v) => (v && String(v).trim() ? String(v).trim() : null);

const getBaseCode = (clubMemberCode) => {
  const code = String(clubMemberCode || "").trim();
  const m = code.match(/^\d+/);
  return m ? m[0] : code;
};

const typeIcons = {
  "Building/Maintenance": (
    <BuildCircleIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
  ),
  Landscaping: (
    <NatureIcon fontSize="small" sx={{ mr: 1, color: "success.main" }} />
  ),
  Other: <BugReportIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />,
};

const DependentsList = ({ baseCode, onPreview }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!baseCode) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await api.get(`/members/${encodeURIComponent(baseCode)}/dependents`);
        if (!cancelled) {
          setRows(Array.isArray(res?.data?.dependents) ? res.data.dependents : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e.message || "Failed to load dependents");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [baseCode]);

  const count = rows.length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.25,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            variant={isXs ? "subtitle1" : "subtitle1"}
            sx={{ fontWeight: 700, color: "#223142" }}
          >
            Household Members
          </Typography>

          {loading ? (
            <Skeleton
              variant="rounded"
              width={48}
              height={24}
              sx={{ borderRadius: 999 }}
            />
          ) : (
            <Chip
              size="small"
              label={`${count} dependents`}
              sx={{
                borderRadius: "999px",
                backgroundColor: "rgba(47,99,173,0.10)",
                color: "#2f63ad",
                fontWeight: 700,
              }}
            />
          )}
        </Stack>

        <Tooltip title={open ? "Collapse" : "Expand"}>
          <IconButton size={isXs ? "small" : "medium"} onClick={() => setOpen((v) => !v)}>
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={open}>
        {loading ? (
          <Stack spacing={1}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 3 }} />
            ))}
          </Stack>
        ) : error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No dependents found for base {baseCode}.
          </Typography>
        ) : (
          <List dense={isXs} sx={{ px: 0, mt: 1 }}>
            {rows.map((d) => {
              const first = (d.FirstName || "").toString().trim();
              const last = (d.LastName || "").toString().trim();
              const name = `${first} ${last}`.trim() || d.ClubMemberCode;
              const file = (d.PhotoGraphicFileName || "").toString().trim();
              const avatarSrc =
                file ? `${API_BASE_URL}/images/${encodeURIComponent(file)}` : undefined;
              const email = trimOrNull(d.EmailAddress);
              const phone = trimOrNull(d.PhoneNumber);

              return (
                <ListItem
                  key={d.ClubMemberCode}
                  sx={{
                    borderRadius: "14px",
                    mb: 1,
                    px: 1,
                    py: 1,
                    alignItems: "flex-start",
                    backgroundColor: "rgba(255,255,255,0.88)",
                    border: "1px solid rgba(47,99,173,0.08)",
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: isXs ? 42 : 54 }}>
                    <Avatar
                      src={avatarSrc}
                      alt={name}
                      sx={{
                        width: { xs: 34, sm: 42 },
                        height: { xs: 34, sm: 42 },
                        cursor: avatarSrc ? "pointer" : "default",
                      }}
                      onClick={() => avatarSrc && onPreview?.(avatarSrc, name)}
                    >
                      {!avatarSrc && (name?.[0] || "?")}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ rowGap: 0.5 }}
                      >
                        <Typography
                          variant={isXs ? "body1" : "subtitle2"}
                          fontWeight={700}
                          sx={{ color: "#223142" }}
                        >
                          {name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`Code: ${d.ClubMemberCode}`}
                          sx={{
                            borderRadius: "999px",
                            backgroundColor: "rgba(47,99,173,0.08)",
                            color: "#2f63ad",
                            fontWeight: 600,
                          }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack
                        direction="row"
                        spacing={2}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.5, "& a, & p": { wordBreak: "break-word" } }}
                      >
                        {email ? (
                          <Link href={`mailto:${email}`} underline="hover" variant="body2">
                            {email}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No email
                          </Typography>
                        )}

                        {phone ? (
                          <Link href={`tel:${phone}`} underline="hover" variant="body2">
                            {phone}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No phone
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Collapse>
    </Box>
  );
};
const OpenTicketsSection = ({ unresolvedTickets = [] }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(true);

  const ticketsByType = unresolvedTickets.reduce((acc, ticket) => {
    const type = ticket.type_name || "Other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(ticket);
    return acc;
  }, {});

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.25,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <AssignmentIcon sx={{ color: "#2f63ad", fontSize: 20 }} />

          <Typography
            variant={isXs ? "subtitle1" : "subtitle1"}
            sx={{ fontWeight: 700, color: "#223142" }}
          >
            Other Open Tickets
          </Typography>

          <Chip
            size="small"
            label={`${unresolvedTickets.length} open`}
            sx={{
              borderRadius: "999px",
              backgroundColor: "rgba(47,99,173,0.10)",
              color: "#2f63ad",
              fontWeight: 700,
            }}
          />
        </Stack>

        <Tooltip title={open ? "Collapse" : "Expand"}>
          <IconButton
            size={isXs ? "small" : "medium"}
            onClick={() => setOpen((v) => !v)}
          >
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={open}>
        {unresolvedTickets.length > 0 ? (
          <Stack spacing={1.25}>
            {Object.entries(ticketsByType).map(([type, tickets]) => (
              <Box
                key={type}
                sx={{
                  borderRadius: "14px",
                  p: 1.5,
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(255,255,255,0.88)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {typeIcons[type] || (
                    <BuildCircleIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "primary.main" }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    {type}: {tickets.length} open
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: 4 }}>
                  {tickets.map((t) => (
                    <Button
                      key={t.order_id}
                      component={RouterLink}
                      to={`/home/workorders/${t.order_id}`}
                      size="small"
                      variant="contained"
                      color="primary"
                      sx={{
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                        textTransform: "none",
                        px: 1.75,
                        py: 0.5,
                        minWidth: "auto",
                        boxShadow: "none",
                      }}
                    >
                      WO-{t.order_id}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "success.main", fontWeight: 600 }}
          >
            🎉 No other open tickets
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

const MemberProfileCard = ({ member, unresolvedTickets = [] }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [preview, setPreview] = useState({
    open: false,
    src: "",
    title: "",
  });

  const openPreview = (src, title) => {
    if (!src) return;
    setPreview({ open: true, src, title });
  };

  const closePreview = () =>
    setPreview((prev) => ({
      ...prev,
      open: false,
    }));

  const baseCode = useMemo(
    () => getBaseCode(member?.ClubMemberCode),
    [member?.ClubMemberCode]
  );

  if (!member) return null;

  const photoUrl = member.PhotoGraphicFileName?.trim?.()
    ? `${API_BASE_URL}/images/${encodeURIComponent(
        member.PhotoGraphicFileName.trim()
      )}`
    : undefined;

  const email = trimOrNA(member.EmailAddress);
  const address = trimOrNA(member.FullAddress);
  const phone = trimOrNull(member.PhoneNumber);
  const altPhone = trimOrNull(member.AlternatePhoneNumber);

  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`
      : null;

  const altAddress =
    trimOrNull(member.AlternateAddress) ??
    (Array.isArray(member.AlternateAddresses) && member.AlternateAddresses.length
      ? [
          member.AlternateAddresses[0]?.AddressLine1,
          member.AlternateAddresses[0]?.AddressLine2,
          member.AlternateAddresses[0]?.AddressLine3,
          member.AlternateAddresses[0]?.AddressLine4,
          member.AlternateAddresses[0]?.AddressLine5,
        ]
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
          .join(" ")
      : null);

  const altMapsLink = altAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        altAddress
      )}`
    : null;

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const infoRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.75,
    minWidth: 0,
    height: "100%",
  };

  const iconWrapSx = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const labelSx = {
    color: "#8a94a6",
    display: "block",
    lineHeight: 1.2,
    mb: 0.25,
  };

  const valueSx = {
    color: "#223142",
    fontWeight: 600,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  };

  return (
    <>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 2,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
            textAlign: "center",
          }}
        >
          <Avatar
            src={photoUrl}
            alt={member.FirstName}
            onClick={() =>
              openPreview(
                photoUrl,
                `${member.FirstName || ""} ${member.LastName || ""}`.trim()
              )
            }
            sx={{
              width: 112,
              height: 112,
              margin: "0 auto",
              mb: 1.75,
              border: "4px solid rgba(47,99,173,0.16)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              cursor: photoUrl ? "pointer" : "default",
            }}
          >
            {!photoUrl && (member.FirstName?.[0] || "?")}
          </Avatar>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              lineHeight: 1.2,
            }}
          >
            {`${member.FirstName || ""} ${member.LastName || ""}`.trim() || "Member"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
              mt: 0.5,
            }}
          >
            Member Code: {member.ClubMemberCode || "N/A"}
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              justifyContent: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={member.ClubStatusRuleCode || "Unknown"}
              color="success"
              size="small"
              icon={<VerifiedUserIcon sx={{ color: "white !important" }} />}
              sx={{
                fontWeight: 700,
                height: 30,
                borderRadius: "999px",
              }}
            />

            {unresolvedTickets.length > 0 && (
              <Chip
                label={`${unresolvedTickets.length} open tickets`}
                size="small"
                sx={{
                  fontWeight: 700,
                  height: 30,
                  borderRadius: "999px",
                  backgroundColor: "rgba(217,119,6,0.12)",
                  color: "#b45309",
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PersonIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Member
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {member.ClubMemberCode || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <WcIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Gender
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {member.Gender || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Birth Date
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {formatDate(member.BirthDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Join Date
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {formatDate(member.JoinDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <EmailIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Email
                  </Typography>
                  {email !== "N/A" ? (
                    <Link
                      href={`mailto:${email}`}
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {email}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PhoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Phone
                  </Typography>
                  {phone ? (
                    <Link
                      href={`tel:${phone}`}
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {phone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PhoneAndroidIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Alt Phone
                  </Typography>
                  {altPhone ? (
                    <Link
                      href={`tel:${altPhone}`}
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {altPhone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <LocationOnIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Address
                  </Typography>
                  {mapsLink ? (
                    <Link
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {address}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {member?.ClubMemberCode && (
              <Grid item xs={12}>
                <MiniMap clubMemberCode={member.ClubMemberCode} height={160} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <LocationOnIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Alternate Address
                  </Typography>
                  {altMapsLink ? (
                    <Link
                      href={altMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {altAddress}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

          <Box
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              p: 1.5,
            }}
          >
            <DependentsList baseCode={baseCode} onPreview={openPreview} />
          </Box>

          <Divider sx={{ my: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

          <Box
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              p: 1.5,
            }}
          >
            <OpenTicketsSection unresolvedTickets={unresolvedTickets} />
          </Box>
        </Box>
      </Card>

      <Dialog
        open={preview.open}
        onClose={closePreview}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: "relative", bgcolor: "black" }}>
          <IconButton
            aria-label="Close"
            onClick={closePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <DialogContent
            sx={{
              p: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {preview.src ? (
              <Box
                component="img"
                src={preview.src}
                alt={preview.title || "Member photo"}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: { xs: "100vh", md: "80vh" },
                  objectFit: "contain",
                  backgroundColor: "black",
                }}
              />
            ) : (
              <Box sx={{ p: 4 }}>
                <Typography>No image available.</Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};

export default MemberProfileCard;